import type { FC, ReactNode } from 'react'
import type { Area } from '@/types'

import Link from 'next/link'
import { House } from 'lucide-react'
import { homesHref } from '@/lib/search'
import { LogoMark } from '@/components/generals/logo'
import { siteConfig } from '@/constants/site'

interface SiteFooterProps {
    areas?: Area[]
}

const exploreLinks = [
    { href: '/homes', label: 'All homes for sale' },
    { href: '/style-match', label: 'Style match' },
    { href: '/saved', label: 'Saved homes' }
]

const companyLinks = [
    { href: '/sell', label: 'Sell your home' },
    { href: '/sell#valuation', label: 'Free home value estimate' },
    { href: '/agents', label: 'Our agents' },
    { href: '/agents#contact', label: 'Contact us' }
]

export const SiteFooter: FC<SiteFooterProps> = ({ areas = [] }): ReactNode => (
    <footer id='site-footer' className='border-t border-border bg-secondary'>
        <div className='mx-auto grid max-w-[1760px] gap-10 px-4 py-12 sm:px-6 md:grid-cols-2 lg:grid-cols-[1.4fr_1fr_1fr_1fr] lg:px-10'>
            <div className='max-w-sm'>
                <div className='flex items-center gap-2'>
                    <LogoMark />
                    <span className='text-lg font-extrabold tracking-tight text-primary'>viewfinder</span>
                </div>

                <p className='mt-4 text-sm leading-relaxed text-muted-foreground'>
                    {siteConfig.name} is a California real estate brokerage built around how people really shop for homes: by looking.
                </p>
            </div>

            <nav aria-label='Explore homes'>
                <h2 className='text-sm font-semibold'>Explore</h2>
                <ul className='mt-4 space-y-3 text-sm'>
                    {exploreLinks.map(link => (
                        <li key={link.href}>
                            <Link href={link.href} className='text-muted-foreground hover:text-foreground hover:underline'>{link.label}</Link>
                        </li>
                    ))}
                </ul>
            </nav>

            {areas.length > 0 && (
                <nav aria-label='Areas'>
                    <h2 className='text-sm font-semibold'>Areas</h2>
                    <ul className='mt-4 space-y-3 text-sm'>
                        {areas.map(area => (
                            <li key={area.id}>
                                <Link href={homesHref({ area: area.slug })} className='text-muted-foreground hover:text-foreground hover:underline'>
                                    <span data-mdlfy-cms={`areas:${area.id}:name`}>{area.name}</span>
                                </Link>
                            </li>
                        ))}
                    </ul>
                </nav>
            )}

            <nav aria-label='Company'>
                <h2 className='text-sm font-semibold'>Company</h2>
                <ul className='mt-4 space-y-3 text-sm'>
                    {companyLinks.map(link => (
                        <li key={link.href}>
                            <Link href={link.href} className='text-muted-foreground hover:text-foreground hover:underline'>{link.label}</Link>
                        </li>
                    ))}
                </ul>
            </nav>
        </div>

        <div className='border-t border-border'>
            <div className='mx-auto flex max-w-[1760px] flex-col gap-3 px-4 py-6 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between sm:px-6 lg:px-10'>
                <p>
                    &copy; <span suppressHydrationWarning>{new Date().getFullYear()}</span> {siteConfig.name}. All rights reserved.
                </p>

                <p className='flex items-center gap-2'>
                    <House className='size-4' aria-hidden='true' />
                    Equal Housing Opportunity
                </p>
            </div>
        </div>
    </footer>
)
